import moment from 'moment';
import { RemoteConfigUtils } from '../../../utils/RemoteConfigUtils';

const DATE_FORMAT = 'ddd, MMM DD, YYYY';

/**
 * Whether the Remove control should render for a row.
 *
 * The monolith computes this server-side from the shift end plus the
 * `DELETE_REVIEW_THRESHOLD` Remote Config value; organization-service's payload
 * carries no such flag, so the portal evaluates the same gate against the same
 * config key. That keeps one source of truth for the window — nobody has to
 * remember to change a number in two places — at the cost of the portal needing
 * the key to be readable from a web client.
 *
 * **The threshold is a delay, not an expiry.** A review becomes removable once
 * `shiftEnd + DELETE_REVIEW_THRESHOLD` has passed, and stays removable after
 * that — it is not a window that closes. Transcribed from the monolith's own
 * comparison, whose local names say the same thing (`hideRatingThreshold`,
 * `allowRemoveAt`):
 *
 *     const allowRemoveAt = jobDateTimeEnd.clone().add(hideRatingThreshold, 'hours');
 *     data.isRemovable = allowRemoveAt.isValid() && moment().isSameOrAfter(allowRemoveAt);
 *     // tempmee_server/src/services/user/users-review.types.ts:81-92
 *
 * So a shift that ended an hour ago is *not* removable; one that ended a month
 * ago is. Getting this backwards is silent — the control simply appears on the
 * wrong rows — which is why the boundary is tested from both sides.
 *
 * Two reasons to say no, both rendering the same way: no control at all. That
 * single affordance is a decision recorded on GOTU-6970 and reaffirmed on
 * GOTU-7338 — a disabled icon was considered as a visible signal and rejected.
 *
 * 1. No parseable `shiftEndUtc`. Absent, null or malformed all mean the gate
 *    cannot be evaluated, and the safe answer to that is "not removable". The
 *    monolith does the same via `jobDateTimeEnd.isValid()`.
 * 2. No usable threshold. An unset key, or Firebase not configured at all,
 *    reads as 0 (see `RemoteConfigUtils`), which the monolith also treats as
 *    "leave it unremovable" via its `if (hideRatingThreshold)` guard. A
 *    negative value is refused here too, where the monolith would compute a
 *    time in the past and allow removal immediately.
 *
 * Note (2) means **nothing is removable wherever Firebase is unconfigured** —
 * local development, and any test that does not mock `RemoteConfigUtils`.
 *
 * **There is deliberately no guard on the id.** Deletion goes to
 * organization-service's `DELETE /admin/office-reviews/{id}`, keyed by its own
 * `reviewId`, which every row always has — so every office review is removable
 * once the delay has elapsed, including one captured seconds ago that has not
 * round-tripped through publish and ingest yet. An earlier draft of this gate
 * refused rows with a null `monolithReviewId`, which was correct only while the
 * delete was addressed by the monolith's id.
 *
 * @param {import('../types/OfficeReview.api').IOfficeReviewApiModel} row
 * @param {number | Date} [now] - Injectable for tests; defaults to the current time
 * @returns {boolean}
 */
export const isReviewRemovable = (row, now = Date.now()) => {
  const shiftEnd = moment(row?.shiftEndUtc, moment.ISO_8601);
  if (!row?.shiftEndUtc || !shiftEnd.isValid()) return false;

  const thresholdHours = RemoteConfigUtils.getDeleteReviewThresholdInHours();
  if (!Number.isFinite(thresholdHours) || thresholdHours <= 0) return false;

  const allowRemoveAt = shiftEnd.clone().add(thresholdHours, 'hours');

  return moment(now).isSameOrAfter(allowRemoveAt);
};

/**
 * Formats a date for a table cell, or an empty string when there is nothing to
 * format.
 *
 * The cells render a blank rather than a placeholder, so an absent or malformed
 * date must not become the string `"Invalid date"` — which is what
 * `moment(null).format()` produces.
 */
const toDateLabel = (isoString) => {
  if (!isoString) return '';

  const parsed = moment(isoString, moment.ISO_8601);

  return parsed.isValid() ? parsed.format(DATE_FORMAT) : '';
};

/**
 * The reviewer's display name, from whichever name parts are present.
 *
 * Joining only the present parts is what fixes the `"Ada N/A"` the legacy cell
 * produced: it rendered `{first_name} {last_name || 'N/A'}`, so a reviewer with
 * a first name and no last name read as "Ada N/A". Both absent yields an empty
 * string, matching how the design treats every other missing value.
 */
const toReviewerName = ({ reviewerFirstName, reviewerLastName }) =>
  [reviewerFirstName, reviewerLastName].filter(Boolean).join(' ');

/**
 * The "Would work here again?" cell's text.
 *
 * Three states, and the distinction matters: `false` is a professional saying
 * *no*, while absent is a review captured before GOTU-6521 shipped the field,
 * which the design renders as an empty cell. A truthiness check
 * (`value ? 'Yes' : ''`) would collapse those two together and every historical
 * row would read as a rejection.
 *
 * The wire shape is `*bool` with `omitempty`, so nil arrives as an **absent
 * key** rather than as null — hence an explicit true/false test rather than a
 * null check.
 */
const toWouldWorkHereAgainLabel = (wouldWorkHereAgain) => {
  if (wouldWorkHereAgain === true) return 'Yes';
  if (wouldWorkHereAgain === false) return 'No';

  return '';
};

/**
 * Maps one organization-service row into the shape the office Reviews table
 * renders.
 *
 * Dates and labels resolve here rather than in the table, so every
 * null-handling branch sits in the domain layer where the coverage floor
 * applies and the table stays declarative — the contract
 * `Campaigns/CampaignsTable.jsx` documents as "rows arrive already mapped …
 * nothing here sorts or reformats".
 *
 * `averageRating` is deliberately absent, and that is not a gap: GOTU-7338
 * deletes the AVG Rating column because it already read `N/A` on every office
 * row. `averageRating` exists only inside the monolith's
 * `ProfessionalRatingSchema`, and `reviewDocToResponse` spreads
 * `professionalRating` onto the row while keeping `officeRating` nested
 * (`tempmee_server/src/models/review.ts:145-155`), so a professional→office
 * review never had a top-level `averageRating` to begin with.
 *
 * @param {import('../types/OfficeReview.api').IOfficeReviewApiModel} row
 * @param {number | Date} [now] - Injectable for tests
 * @returns {import('../types/OfficeReview').IOfficeReviewViewEntity}
 */
const toOfficeReviewViewEntity = (row, now) => ({
  // organization-service's own id, which is always present. This is the React
  // key: `monolithReviewId` is null on a review that has not round-tripped
  // through publish and ingest yet, and two such rows on one page would collide
  // on the same key and be dropped or reused by React.
  id: row.reviewId,

  // Carried for diagnostics only — nothing renders or acts on it. Deletion
  // addresses `id` above (organization-service's own `reviewId`), so this is
  // just the link to the counterpart Mongo document, and null until the review
  // has round-tripped through publish and ingest.
  monolithReviewId: row.monolithReviewId ?? null,

  jobId: row.jobId,

  // The Job Detail Rating tab's office card uses this as its heading. The
  // office Reviews table does not — it is already on the office's own page,
  // which is why this went unmapped until GOTU-7339 needed it.
  officeName: row.officeName ?? '',

  shiftDateLabel: toDateLabel(row.shiftStartUtc),
  createdDateLabel: toDateLabel(row.createdAtUtc),
  reviewerName: toReviewerName(row),
  rating: row.rating,
  wouldWorkHereAgainLabel: toWouldWorkHereAgainLabel(row.wouldWorkHereAgain),

  // "Office Feedback" and "Private Feedback" are the column labels; these are
  // the API's field names and they stay. Both are non-pointer strings on the
  // wire, so they are always present and blank as `''` rather than null.
  publicFeedback: row.publicFeedback,
  confidentialFeedback: row.confidentialFeedback,

  isRemovable: isReviewRemovable(row, now),
});

/**
 * Maps the list envelope.
 *
 * Defends against a missing `reviews` array so a malformed page renders an
 * empty table rather than throwing.
 *
 * @param {import('../types/OfficeReview.api').IGetOfficeReviewsApiResponse} response
 * @param {number | Date} [now] - Injectable for tests
 * @returns {import('../types/OfficeReview').IGetOfficeReviewsResult}
 */
const toGetOfficeReviewsResult = (response, now) => ({
  response: (response?.reviews ?? []).map((row) => toOfficeReviewViewEntity(row, now)),
  total: response?.total ?? 0,
  limit: response?.limit,
  page: response?.page,
});

export const OfficeReviewAdapters = {
  toOfficeReviewViewEntity,
  toGetOfficeReviewsResult,
};
